import { useCallback, useEffect, useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { supabase } from "../../../supabase";
import { sendInviteFromSoftwall, setSessionUser } from "../../Reducers/actions/authActions";
import { validateEmail } from "../../../helpers/utilities";
import { useDispatch, useSelector } from "react-redux";
import Icon from "../../components/Icon/Icon";
import HykLogo from "../../components/Icon/HykLogo";
import "./AuthCommLink.scss";
import { startGoogleAuth } from "./googleAuth";
import GoogleButtonContent from "./GoogleButtonContent";

export default function Register() {
  const session = useSelector((state) => state.auth?.session);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [finishAnimation, setFinishAnimation] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const keyboardVisible = false;

  useEffect(() => {
    const checkUser = async () => {
      setLoading(true);
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        dispatch(setSessionUser(data.session));
        navigate("/");
      } else {
        setLoading(false);
      }
    };
    checkUser();
  }, [dispatch, navigate]);

  const validateForm = useCallback(() => {
    if (!(email && email.length > 0)) {
      setErrorMessage("Email is required.");
      return false;
    }
    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return false;
    }
    setErrorMessage(null);
    return true;
  }, [email]);

  useEffect(() => {
    if (hasAttemptedSubmit) validateForm();
  }, [email, hasAttemptedSubmit, validateForm]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setHasAttemptedSubmit(true);
    if (!validateForm()) return;

    setLoadingRegister(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    sendInviteFromSoftwall(email).then(({ success, message }) => {
      setLoadingRegister(false);
      if (success) {
        setSuccessMessage("Invitation sent successfully! Please check your email for the invitation.");
      } else {
        if (message?.includes?.("User with this email already exists")) {
          setErrorMessage(message);
          setSuccessMessage(null);
        } else {
          setErrorMessage(message || "Something went wrong. Please try again.");
        }
      }
    });
  };

  const handleGoogleRegister = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setHasAttemptedSubmit(true);
    setLoadingGoogle(true);
    const returnTo = `${location.pathname}${location.search || ""}`;
    const result = await startGoogleAuth(returnTo);
    if (!result.success) {
      setErrorMessage(result.message);
      setLoadingGoogle(false);
      return;
    }
    window.location.href = result.url;
  };

  const isGoogleAuthEnabled = import.meta.env.VITE_ENABLE_GOOGLE_AUTH !== "false";

  useEffect(() => {
    if (!loading && !session) {
      setTimeout(() => {
        setAnimated(true);
        setTimeout(() => {
          setFinishAnimation(true);
        }, 1000);
      }, 1000);
    }
  }, [loading, session]);

  if (session) return <Navigate to="/" replace />;

  return (
    <div className="px-16 pt-16 login-container bg-red">
      {!finishAnimation && (
        <div className={`first-screen ${finishAnimation ? "finish-animation" : ""}`}>
          <HykLogo
            width={120}
            height={120}
            className={animated ? "image-expand" : ""}
            color="#FF3232"
          />
        </div>
      )}

      {finishAnimation && (
        <div
          className="d-flex aic jcfs bg-red f-column p-fixed"
          style={{ height: "100%", top: 0, left: 0 }}
        >
          {!keyboardVisible && (
            <div className="d-flex aic jcc my-40 pb-24">
              <Icon alias="hyk" width={48} color="#FFF" className="hyk-icon-animate" />
            </div>
          )}
          <div className="auth-container login-animated-container px-16 animate-slide-up">
            <p className="fs-md2 fw-bold mb-40 mt-0">JOIN HYK</p>
            {hasAttemptedSubmit && errorMessage && <p className="error-message">{errorMessage}</p>}
            {successMessage && <p className="fs-regular fw-regular ta-center fg-green mb-16">{successMessage}</p>}
            <form onSubmit={handleRegister} className="auth-form">
              <input
                ref={(el) => {
                  if (el) {
                    const checkAutoFill = () => {
                      if (el.value && el.value !== email) setEmail(el.value);
                    };
                    checkAutoFill();
                    setTimeout(checkAutoFill, 100);
                    setTimeout(checkAutoFill, 500);
                    setTimeout(checkAutoFill, 1000);
                  }
                }}
                className="input-main"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onInput={(e) => {
                  if (e.target.value !== email) setEmail(e.target.value);
                }}
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                required
                aria-label="Email"
              />
              <button
                type="submit"
                className="btn red-btn btn-full-v"
                disabled={loadingRegister || loadingGoogle || !!errorMessage}
                aria-label="Register"
              >
                {loadingRegister ? "Sending invitation..." : "Register"}
              </button>
              {isGoogleAuthEnabled && (
                <button
                  type="button"
                  className="btn btn-full-v mt-16 google-auth-btn"
                  disabled={loadingRegister || loadingGoogle}
                  onClick={handleGoogleRegister}
                  aria-label="Continue with Google"
                >
                  <GoogleButtonContent loading={loadingGoogle} />
                </button>
              )}
            </form>
            <p className="auth-text mb-0 fg-crea fw-medium mt-24">
              Already have an account?{" "}
              <span
                className="fg-red-pink fw-bold mt-0 td-underline c-pointer"
                onClick={() => navigate("/login")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") navigate("/login");
                }}
                role="button"
                tabIndex={0}
                aria-label="Log in"
              >
                Log in
              </span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
